import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Search, ArrowRight } from 'lucide-react';
import { generalFaqs, BOOKING_URL } from '../data/services';
import { Input } from '../components/ui/input';

export const FAQPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Filter FAQs based on search query
  const filteredFaqs = generalFaqs.map(category => ({
    ...category,
    questions: category.questions.filter(
      faq => 
        faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => 
    activeCategory === 'all' || category.category === activeCategory
  ).filter(category => category.questions.length > 0);

  // Get unique categories
  const categories = ['all', ...generalFaqs.map(c => c.category)];

  return (
    <main data-testid="faq-page" className="pt-24 bg-chalk min-h-screen">
      {/* Hero */}
      <section className="py-16 lg:py-24 text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-cinzel text-amaranth text-xs tracking-[0.3em] mb-4">
            HAVE QUESTIONS?
          </p>
          <h1 className="font-serif text-dark text-4xl lg:text-6xl mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-dark-secondary max-w-2xl mx-auto mb-8">
            Find answers to common questions about our treatments, booking process, 
            and what to expect during your visit to Aphrodite Med Spa.
          </p>

          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-secondary" size={20} />
            <Input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="faq-search"
              className="pl-12 py-6 bg-light border-dark/10 text-dark"
            />
          </div>
        </motion.div>
      </section>

      {/* Category Filter */}
      <section className="px-6 mb-12">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-3">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              data-testid={`faq-category-${category.replace(/\s+/g, '-').toLowerCase()}`}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === category
                  ? 'bg-amaranth text-white'
                  : 'bg-light text-dark hover:bg-thulian/20'
              }`}
            >
              {category === 'all' ? 'All Questions' : category}
            </button>
          ))}
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          {filteredFaqs.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <p className="text-dark-secondary mb-4">No questions found matching your search.</p>
              <button 
                onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
                className="text-amaranth hover:underline"
              >
                Clear filters
              </button>
            </motion.div>
          ) : (
            filteredFaqs.map((category, catIndex) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: catIndex * 0.1 }}
                className="mb-10"
              >
                <h2 className="font-serif text-dark text-2xl mb-4 pb-2 border-b border-dark/10">
                  {category.category}
                </h2>
                <div className="space-y-2">
                  {category.questions.map((faq, faqIndex) => {
                    const uniqueKey = `${catIndex}-${faqIndex}`;
                    return (
                      <div
                        key={uniqueKey}
                        className="bg-light overflow-hidden"
                      >
                        <button
                          onClick={() => setOpenFaq(openFaq === uniqueKey ? null : uniqueKey)}
                          data-testid={`general-faq-${catIndex}-${faqIndex}`}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-chalk/50 transition-colors"
                        >
                          <span className="font-medium text-dark pr-4">{faq.q}</span>
                          <ChevronDown 
                            size={20} 
                            className={`text-amaranth flex-shrink-0 transition-transform duration-300 ${
                              openFaq === uniqueKey ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        <AnimatePresence>
                          {openFaq === uniqueKey && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <p className="px-5 pb-5 text-dark-secondary leading-relaxed">
                                {faq.a}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))
          )}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="px-6 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto bg-olive/10 p-8 lg:p-12 text-center"
        >
          <h2 className="font-serif text-dark text-3xl mb-4">
            Still Have Questions?
          </h2>
          <p className="text-dark-secondary mb-8 max-w-xl mx-auto">
            Our team is here to help. Contact us directly or book a complimentary 
            consultation to discuss your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="faq-book-btn"
              className="btn-luxury inline-flex items-center justify-center gap-2 bg-amaranth text-white px-8 py-4 rounded-full font-medium"
            >
              Book Consultation
              <ArrowRight size={18} />
            </a>
            <Link
              to="/contact"
              data-testid="faq-contact-link"
              className="inline-flex items-center justify-center gap-2 border border-dark/30 text-dark px-8 py-4 rounded-full font-medium hover:bg-dark hover:text-white transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </motion.div>
      </section>
    </main>
  );
};

export default FAQPage;
